<template>
  <main class="blog-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1). -->
    <SeoHead :seo="content.seo" />

    <!--
      Branded placeholder listing page for the hidden Blog/News section
      (Requirement 12.1). This page always renders when reached directly,
      regardless of the Blog Feature_Flag — the flag only gates the navigation
      link, never the route itself (Requirements 12.1, 12.2, 12.4).
    -->
    <section class="blog-hero" aria-labelledby="blog-hero-heading">
      <v-container>
        <ScrollReveal>
          <h1 id="blog-hero-heading" class="blog-heading">
            {{ content.hero.heading }}
          </h1>
          <p class="blog-intro">{{ content.hero.intro }}</p>
        </ScrollReveal>
      </v-container>
    </section>

    <!--
      Placeholder listing grid. Renders the brand-styled "coming soon" article
      cards so the section reads as a listing page while no real articles exist
      yet. When the Blog section is enabled later, this grid is replaced by a
      data-driven list without changes to other pages (Requirement 12.4).
    -->
    <section class="blog-listing" aria-labelledby="blog-listing-heading">
      <v-container>
        <ScrollReveal>
          <h2 id="blog-listing-heading" class="blog-section-heading">
            {{ content.listing.heading }}
          </h2>
          <p class="blog-listing__intro">{{ content.listing.intro }}</p>
        </ScrollReveal>
        <v-row>
          <v-col
            v-for="(placeholder, index) in content.listing.placeholders"
            :key="index"
            cols="12"
            md="4"
          >
            <ScrollReveal :delay="index * 100">
              <v-card class="blog-card" variant="flat" height="100%">
                <div class="blog-card__media" aria-hidden="true"></div>
                <v-card-item>
                  <span class="blog-card__category">{{ placeholder.category }}</span>
                  <h3 class="blog-card__title">{{ placeholder.title }}</h3>
                  <p class="blog-card__body">{{ placeholder.body }}</p>
                </v-card-item>
              </v-card>
            </ScrollReveal>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Closing CTA directing visitors to Contact while the blog is empty. -->
    <section class="blog-cta" aria-labelledby="blog-cta-heading">
      <v-container>
        <ScrollReveal>
          <div class="blog-cta__inner">
            <h2 id="blog-cta-heading" class="blog-section-heading">
              {{ content.closingCta.heading }}
            </h2>
            <p class="blog-cta__body">{{ content.closingCta.body }}</p>
            <CtaButton
              :to="content.closingCta.cta.to"
              :label="content.closingCta.cta.label"
            />
          </div>
        </ScrollReveal>
      </v-container>
    </section>
  </main>
</template>

<script>
// BlogPage — branded placeholder listing page for the hidden Blog/News section
// (Requirement 12.1).
//
// The Blog_Section route is always registered so it remains reachable by direct
// URL and renders this placeholder regardless of the Blog Feature_Flag; only the
// navigation link is gated by the flag (Requirements 12.1, 12.2). The page is
// structured so enabling the section later (swapping the placeholder grid for a
// data-driven article list) needs no changes to other pages (Requirement 12.4).
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset (no `<script setup>`).
import SeoHead from '@/components/SeoHead.vue'
import ScrollReveal from '@/components/ScrollReveal.vue'
import CtaButton from '@/components/CtaButton.vue'

export default {
  name: 'BlogPage',
  components: {
    SeoHead,
    ScrollReveal,
    CtaButton,
  },
  data () {
    return {
      // Placeholder copy and SEO for the hidden Blog/News listing page. Kept
      // local to this placeholder; real article content arrives when the
      // section is enabled.
      content: {
        seo: {
          title: 'Blog & News | ThinkSpatial',
          description:
            'Insights, updates, and news from ThinkSpatial on surveying, GIS, engineering, and spatial technology. Articles are coming soon.',
        },
        hero: {
          heading: 'Blog & News',
          intro:
            'Insights and updates from across our surveying, GIS, engineering, and spatial technology work. Our first articles are on the way.',
        },
        listing: {
          heading: 'Coming soon',
          intro:
            'We are preparing stories on the projects, people, and technology shaping spatial work. Check back soon for the latest from ThinkSpatial.',
          placeholders: [
            {
              category: 'Surveying',
              title: 'Articles coming soon',
              body: 'In-depth looks at our civil surveying and asset capture projects.',
            },
            {
              category: 'Technology',
              title: 'Articles coming soon',
              body: 'How automated monitoring, UAV, and spatial software drive better outcomes.',
            },
            {
              category: 'Company',
              title: 'Articles coming soon',
              body: 'News, milestones, and updates from the ThinkSpatial team.',
            },
          ],
        },
        closingCta: {
          heading: 'Have a question in the meantime?',
          body: 'Reach out to the ThinkSpatial team — we are happy to talk through your project.',
          cta: {
            to: '/contact',
            label: 'Contact us',
          },
        },
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.blog-heading {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 16px;
}

.blog-section-heading {
  font-size: 2rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.blog-intro {
  font-size: 1.15rem;
  color: #4a4a4a;
  line-height: 1.7;
  max-width: 720px;
}

.blog-hero {
  padding: 64px 0 32px;
}

.blog-listing {
  padding: 32px 0 64px;
  background-color: #f7f5f4;
}

.blog-listing__intro {
  font-size: 1.05rem;
  color: #4a4a4a;
  margin-bottom: 24px;
  max-width: 720px;
}

.blog-card {
  background-color: #fff;
  border-radius: 12px;
  height: 100%;
  overflow: hidden;
}

.blog-card__media {
  height: 160px;
  background: linear-gradient(135deg, #f29400 0%, #e7511e 100%);
}

.blog-card__category {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #e7511e;
  margin-bottom: 8px;
}

.blog-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #41281b;
  margin-bottom: 8px;
}

.blog-card__body {
  font-size: 0.95rem;
  color: #4a4a4a;
  line-height: 1.6;
}

.blog-cta {
  padding: 64px 0;
  background-color: #41281b;
}

.blog-cta__inner {
  text-align: center;
}

.blog-cta .blog-section-heading {
  color: #fff;
}

.blog-cta__body {
  font-size: 1.1rem;
  color: #e6ded9;
  margin-bottom: 24px;
}
</style>
